import React, { useState, Fragment, useContext } from 'react';
import axios from 'axios';
import {
  TbCaretUpDown,
  TbCaretDownFilled,
  TbCaretUpFilled,
  TbTrash
} from 'react-icons/tb';
import { AuthContext } from '../../context/authContext';
import { Context } from '../../types/interfaces';
import { User } from '../../types/interfaces';
import styles from './UsersList.module.scss';

interface Props {
  users: User[];
}

type SortOrder = 'asc' | 'desc';

type SortBy =
  | 'givenName'
  | 'surname'
  | 'personalNumber'
  | 'role'
  | 'customerId'
  | 'created'
  | 'latestLogin';

const UsersList: React.FC<Props> = ({ users }: Props) => {
  const [usersDisplayed, setUsersDisplayed] = useState(100);
  const context = useContext(AuthContext) as Context;
  const { setUsers } = context;
  const [sortBy, setSortBy] = useState<SortBy>('surname'); // State to track the column to sort by
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc'); // State to track the sorting order
  const [searchTerm, setSearchTerm] = useState(''); // State to track the search term
  const [searchResults, setSearchResults] = useState<User[]>([]); // State to track the search results

  const sortedUsers = users.slice().sort((a, b) => {
    if (sortOrder === 'asc') {
      return a[sortBy] > b[sortBy] ? 1 : -1;
    } else {
      return a[sortBy] < b[sortBy] ? 1 : -1;
    }
  });

  const updateCustomerId = async (
    personalNumber: number,
    customerId: number
  ) => {
    console.log(`Update customerId for: ${personalNumber}`);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_SERVER}/api/tengella/customerId/`,
        {
          personalNumber
        }
      );
      console.log(res.data);
      console.log(res.data.customerId);
      if (res.data && res.data.customerId !== customerId) {
        const updatedUsers = users.map(user =>
          user.personalNumber === personalNumber
            ? { ...user, customerId: res.data.customerId }
            : user
        );
        setUsers(updatedUsers);
        if (searchResults.length > 0) {
          const updatedSearchResults = searchResults.map(user =>
            user.personalNumber === personalNumber
              ? { ...user, customerId: res.data.customerId }
              : user
          );
          setSearchResults(updatedSearchResults);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const deleteUser = async (personalNumber: number) => {
    console.log(`Delete user: ${personalNumber}`);
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_SERVER}/api/users/${personalNumber}`
      );
      console.log(res.data);
      if (res.data && res.data.personalNumber === personalNumber) {
        const updatedUsers = users.filter(
          user => user.personalNumber !== personalNumber
        );
        setUsers(updatedUsers);
        if (searchResults.length > 0) {
          const updatedSearchResults = searchResults.filter(
            user => user.personalNumber !== personalNumber
          );
          setSearchResults(updatedSearchResults);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadMore = () => {
    if (usersDisplayed + 100 <= (users ? users.length : 0)) {
      setUsersDisplayed(usersDisplayed + 100);
    } else {
      setUsersDisplayed(users ? users.length : 0);
    }
  };

  const handleSort = (column: SortBy) => {
    console.log(column);
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    if (searchResults.length > 0) {
      const sortedResults = searchResults.slice().sort((a, b) => {
        if (sortOrder === 'asc') {
          return a[column] > b[column] ? 1 : -1;
        } else {
          return a[column] < b[column] ? 1 : -1;
        }
      });
      setSearchResults(sortedResults);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log(event.target.value);
    setSearchTerm(event.target.value);
    if (event.target.value === '') {
      setSearchResults([]);
    } else {
      const results = users.filter(user =>
        `${user.givenName} ${user.surname} ${user.personalNumber}`
          .toLowerCase()
          .includes(event.target.value.toLowerCase())
      );
      setSearchResults(results);
    }
  };

  return (
    <>
      <input
        type="text"
        placeholder="Search by name or personal number"
        value={searchTerm}
        onChange={handleSearch}
        className={styles.searchInput}
      />
      <table className={styles.usersTable}>
        <thead className={styles.tableHead}>
          <tr>
            <th className={styles.sort} onClick={() => handleSort('givenName')}>
              givenName
              <span className={styles.caret}>
                {sortBy === 'givenName' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th className={styles.sort} onClick={() => handleSort('surname')}>
              surname
              <span className={styles.caret}>
                {sortBy === 'surname' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th
              className={styles.sort}
              onClick={() => handleSort('personalNumber')}
            >
              personalNumber
              <span className={styles.caret}>
                {sortBy === 'personalNumber' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th className={styles.sort} onClick={() => handleSort('role')}>
              role
              <span className={styles.caret}>
                {sortBy === 'role' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th
              className={styles.sort}
              onClick={() => handleSort('customerId')}
            >
              customerId
              <span className={styles.caret}>
                {sortBy === 'customerId' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th className={styles.sort} onClick={() => handleSort('created')}>
              created
              <span className={styles.caret}>
                {sortBy === 'created' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th
              className={styles.sort}
              onClick={() => handleSort('latestLogin')}
            >
              latestLogin
              <span className={styles.caret}>
                {sortBy === 'latestLogin' ? (
                  sortOrder === 'asc' ? (
                    <TbCaretDownFilled />
                  ) : (
                    <TbCaretUpFilled />
                  )
                ) : (
                  <TbCaretUpDown />
                )}
              </span>
            </th>
            <th>Update customerId</th>
            <th>Delete user</th>
          </tr>
        </thead>
        <tbody>
          {(searchResults.length > 0 ? searchResults : sortedUsers).map(
            (user, index) => {
              const createdDate = new Date(user.created);
              const latestLoginDate = new Date(user.latestLogin);
              return index <= usersDisplayed ? (
                <Fragment key={user._id}>
                  <tr>
                    <td>
                      <span>{user.givenName}</span>
                    </td>
                    <td>
                      <span>{user.surname}</span>
                    </td>
                    <td>
                      <span>{user.personalNumber}</span>
                    </td>
                    <td>
                      <span>{user.role}</span>
                    </td>
                    <td className={styles.customerIdCell}>
                      <span>{user.customerId}</span>
                    </td>
                    <td>
                      <span>
                        {createdDate.toLocaleDateString('sv-SE')}{' '}
                        {createdDate.toLocaleTimeString('sv-SE')}
                      </span>
                    </td>
                    <td>
                      <span>
                        {latestLoginDate.toLocaleDateString('sv-SE')}{' '}
                        {latestLoginDate.toLocaleTimeString('sv-SE')}
                      </span>
                    </td>
                    <td className={styles.updateRollButtonCell}>
                      <button
                        className={styles.updateRoleButton}
                        onClick={() =>
                          updateCustomerId(user.personalNumber, user.customerId)
                        }
                      >
                        Update
                      </button>
                    </td>
                    <td className={styles.updateRollButtonCell}>
                      <button
                        className={`${styles.updateRoleButton} ${styles.deleteButton}`}
                        onClick={() => deleteUser(user.personalNumber)}
                      >
                        <TbTrash />
                      </button>
                    </td>
                  </tr>
                  {window.innerWidth < 768 && <hr />}
                </Fragment>
              ) : null;
            }
          )}
        </tbody>
      </table>
      {(searchResults.length === 0 && usersDisplayed < users.length) ||
      (searchResults && usersDisplayed < searchResults.length) ? (
        <button
          className={styles.button}
          type="button"
          onClick={() => loadMore()}
        >
          Ladda fler användare
        </button>
      ) : null}
    </>
  );
};

export default UsersList;
